import React from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { toast } from 'react-toastify';
import CBMediaQuery from '../../shared/CBMediaQuery';
import CBSubHeading from '../../shared/CBSubHeading';
import CBSwitch from '../../shared/form_components/CBSwitch';
import CBSubmitButton from '../../shared/form_components/CBSubmitButton';
import { getCsrfToken, getStyle } from '../../../utils/utils';
import { Generations } from '../../FleetLocationView/fleetMapCore';

const settingsUrl = (workshopId) => `/uiapi/1/workshop/${workshopId}/ezyvin_settings/`;
const limitText = (value) => (value === null || value === undefined ? '' : String(value));

/*
 * OVIO-133 "Valmistajahaut": the workshop admin's own switch and monthly limit for manufacturer
 * history queries. Its own endpoint (not the shared workshop PUT, which every role may send), so
 * it talks to axios directly: the API middleware would toast a generic error over the inline
 * refusal the page shows. Answers for a workshop other than the selected one are dropped.
 */
export class WSManufacturerQueries extends React.Component {
  state = { loaded: null, enabled: true, limit: '', error: '', saving: false };

  // Every load claims a new visit; an answer is used only if its visit is still the latest, so a
  // late answer from an earlier visit (A, then B, then A again) or after unmount is dropped.
  visits = new Generations();

  componentDidMount() {
    this.load();
  }

  componentWillUnmount() {
    this.visits.claim();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedWorkshopId !== this.props.selectedWorkshopId) {
      this.setState({ loaded: null, enabled: true, limit: '', error: '', saving: false });
      this.load();
    } else if (!this.isAdmin(prevProps) && this.isAdmin()) {
      // Opened by URL or refreshed: the workshop list (and with it the role) arrives after mount.
      this.load();
    }
  }

  isAdmin = (props = this.props) => {
    const { workshops, selectedWorkshopId } = props;
    const workshop = (workshops || []).find((ws) => ws.id == selectedWorkshopId);
    return !!workshop && workshop.user_role === 'admin';
  };

  isCurrent = (visit) => this.visits.isCurrent(visit);

  show = (data) => {
    this.setState({ loaded: data, enabled: data.enabled, limit: limitText(data.monthly_limit), error: '' });
  };

  load = () => {
    const { selectedWorkshopId } = this.props;
    const visit = this.visits.claim();
    if (!selectedWorkshopId || !this.isAdmin()) {
      return;
    }
    axios.get(settingsUrl(selectedWorkshopId))
      .then((response) => { if (this.isCurrent(visit)) this.show(response.data); })
      .catch(() => { if (this.isCurrent(visit)) toast.error(this.props.t('genericFetchSaveError', { ns: 'errors' })); });
  };

  save = () => {
    const { t, selectedWorkshopId } = this.props;
    const { loaded, enabled, limit, saving } = this.state;
    if (saving) return;
    const visit = this.visits.latest;
    const typed = limit.trim();
    // At most nine digits: a longer number would parse to Infinity, which JSON sends as null.
    if (typed !== '' && !/^[1-9][0-9]{0,8}$/.test(typed)) {
      this.setState({ error: t('manufacturerQueriesNumber') });
      return;
    }
    const monthlyLimit = typed === '' ? null : parseInt(typed, 10);
    // Only what the admin changed: an unchanged limit above a lowered cap must not block "off".
    const body = {};
    if (enabled !== loaded.enabled) body.enabled = enabled;
    if (monthlyLimit !== loaded.monthly_limit) body.monthly_limit = monthlyLimit;
    if (Object.keys(body).length === 0) return;
    this.setState({ saving: true });
    axios.patch(settingsUrl(selectedWorkshopId), body, { headers: { 'X-CSRFToken': getCsrfToken() } })
      .then((response) => {
        if (!this.isCurrent(visit)) return;
        this.show(response.data);
        this.setState({ saving: false });
        toast.success('Tiedot tallennettiin onnistuneesti!'); // the API middleware's own success text
      })
      .catch((error) => {
        if (!this.isCurrent(visit)) return;
        const data = error && error.response && error.response.data;
        const message = data && ((data.monthly_limit && data.monthly_limit[0]) || (data.enabled && data.enabled[0]));
        this.setState({ saving: false, error: message || t('genericFetchSaveError', { ns: 'errors' }) });
      });
  };

  toggle = (value) => {
    // Off sets the field back to the stored limit: an unsaved, maybe invalid draft must never block
    // saving "off", and the field is disabled, so it could not be corrected there anyway.
    this.setState((state) => (value
      ? { enabled: true, error: '' }
      : { enabled: false, error: '', limit: limitText(state.loaded.monthly_limit) }));
  };

  renderForm(screenSize) {
    const { t } = this.props;
    const { loaded, enabled, limit, error, saving } = this.state;
    const capZero = loaded.ovio_limit === 0;
    const fieldOff = !enabled || capZero;
    const lowered = loaded.monthly_limit !== null && loaded.monthly_limit > loaded.ovio_limit;
    const dim = fieldOff ? { opacity: 0.45 } : {};
    return (
      <React.Fragment>
        <div style={getStyle(screenSize, styles, 'switchRow')}>
          <label htmlFor='ezyvin-enabled' className='font-titillium-web-semi-bold' style={styles.label}>
            {t('manufacturerQueriesSwitch')}
          </label>
          <CBSwitch id='ezyvin-enabled' checked={enabled} disabled={saving} onChange={this.toggle} />
        </div>
        {!enabled && <p style={styles.text}>{t('manufacturerQueriesOffHelp')}</p>}
        <div style={dim}>
          <label htmlFor='ezyvin-monthly-limit' className='font-titillium-web-semi-bold' style={styles.fieldLabel}>
            {t('manufacturerQueriesOwnLimit')}
          </label>
          <input
            id='ezyvin-monthly-limit'
            name='monthly_limit'
            type='text'
            inputMode='numeric'
            value={limit}
            placeholder={t('manufacturerQueriesPlaceholder')}
            disabled={fieldOff || saving}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby='ezyvin-monthly-limit-help'
            onChange={(event) => this.setState({ limit: event.target.value, error: '' })}
            style={error ? { ...styles.input, ...styles.inputError } : styles.input}
          />
          <p id='ezyvin-monthly-limit-help' style={styles.help}>
            {capZero ? t('manufacturerQueriesZero') : t('manufacturerQueriesLimitHelp', { ovioLimit: loaded.ovio_limit })}
          </p>
        </div>
        {error && (
          <div role='alert' style={{ ...styles.banner, ...styles.bannerError }}>
            <span aria-hidden='true' style={styles.bannerIcon}>!</span>
            <span className='font-titillium-web-semi-bold'>{error}</span>
          </div>
        )}
        {!error && lowered && (
          <div role='status' style={{ ...styles.banner, ...styles.bannerInfo }}>
            <span aria-hidden='true' style={styles.bannerIcon}>i</span>
            <span className='font-titillium-web-semi-bold'>
              {t('manufacturerQueriesLowered', { effectiveLimit: loaded.effective_limit })}
            </span>
          </div>
        )}
        <div style={styles.saveRow}>
          <CBSubmitButton disabled={saving} onClick={this.save} text={t('save', { ns: 'common' })} />
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { t } = this.props;
    const admin = this.isAdmin();
    return (
      <CBMediaQuery>
        {(screenSize) => (
          <div style={getStyle(screenSize, styles, 'container')}>
            <CBSubHeading>{t('manufacturerQueriesMenu')}</CBSubHeading>
            {!admin && <p style={styles.text}>{t('manufacturerQueriesAdminOnly')}</p>}
            {admin && <p style={styles.text}>{t('manufacturerQueriesIntro')}</p>}
            {admin && this.state.loaded && this.renderForm(screenSize)}
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    container: { padding: 32, display: 'flex', flexDirection: 'column', maxWidth: 840 },
    switchRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 540, marginTop: 8, marginBottom: 16 },
  },
  label: { color: 'white', fontSize: 16, margin: 0 },
  fieldLabel: { display: 'block', color: 'white', fontSize: 16, marginTop: 8, marginBottom: 8 },
  text: { color: 'white', marginTop: 16, marginBottom: 8 },
  input: {
    width: 192, height: 40, border: 'none', borderRadius: 100, backgroundColor: '#ebebeb',
    color: 'black', fontSize: 14, textAlign: 'center', fontFamily: 'TitilliumWeb-Regular',
  },
  inputError: { boxShadow: '0 0 0 2px #d14b4b' },
  help: { color: 'rgba(255, 255, 255, 0.85)', fontSize: 13, marginTop: 12, marginBottom: 8 },
  banner: {
    display: 'flex', alignItems: 'center', maxWidth: 540, padding: '8px 12px', borderRadius: 4,
    color: 'white', fontSize: 14, marginBottom: 8,
  },
  bannerError: { backgroundColor: '#d14b4b' },
  bannerInfo: { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
  bannerIcon: {
    display: 'inline-flex', justifyContent: 'center', alignItems: 'center', width: 18, height: 18,
    border: '1.5px solid white', borderRadius: '50%', fontSize: 12, marginRight: 10,
  },
  saveRow: { display: 'flex', justifyContent: 'flex-end', maxWidth: 540, marginTop: 16 },
};

const mapStateToProps = (state) => ({
  workshops: state.workshop.workshops,
  selectedWorkshopId: state.workshop.selectedWorkshop,
});

export default connect(mapStateToProps)(translate('WSSettingsView')(WSManufacturerQueries));
